import React, { useMemo, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import propTypes from 'prop-types';
import SubjectCard from '../../../components/SubjectCard';
import AttachmentList from '../../../components/AttachmentList';
import useMe from '../../../hooks/useMe';
import useContext from '../../../hooks/useContext';
import useAssemblyActions from '../../../hooks/useAssemblyActions';
import useAssemblyReadable from '../../../hooks/useAssemblyReadable';

const optionDescriptionAssemblyDetailFragment = gql`
  fragment optionDescriptionAssemblyDetailFragment on Option {
    _id
    description
    votes
  }
`;

const commentsAssemblyDetailFragment = gql`
  fragment commentsAssemblyDetailFragment on Comment {
    user
  }
`;

const getGql = gql`
  query assembly($_id: ID!) {
    assembly(_id: $_id) {
      _id
      description
      condominium
      type
      status
      attachments {
        _id
        name
        path
      }
      subjects {
        _id
        title
        description
        attachments {
          _id
          name
          path
        }
        options {
          ...optionDescriptionAssemblyDetailFragment
        }
        comments {
          ...commentsAssemblyDetailFragment
        }
      }
    }
    condominium {
      name
    }
  }
  ${commentsAssemblyDetailFragment}
  ${optionDescriptionAssemblyDetailFragment}
`;

export default function AssemblyDetail({ match, history }) {
  const [me, fetchMe] = useMe();
  const { isJanitor } = useContext();
  const { data, loading, refetch } = useQuery(getGql, {
    fetchPolicy: 'no-cache',
    skip: !me,
    variables: {
      _id: match.params.assemblyId,
    },
  });

  const { condominium, assembly } = useMemo(() => {
    if (!data) return {};

    return data;
  }, [data]);
  const readableAssembly = useAssemblyReadable(assembly);

  const { open, opening } = useAssemblyActions({
    assembly,
    onCompleted: () => refetch(),
  });

  useEffect(() => {
    fetchMe();
  }, []);

  return (
    <div className="dashboard h-100 d-flex flex-column">
      {!condominium || !assembly || loading ? (
        <div className="justify-content-center d-flex p-3">
          <h4>Carregando</h4>
        </div>
      ) : (
        <>
          <FaArrowLeft onClick={() => history.push('/dashboard')} className="mt-3 back-icon" />

          <div className="justify-content-center d-flex p-3">
            <div className="d-flex flex-column text-center">
              <h4>{condominium.name}</h4>
              <h6>{readableAssembly.type}</h6>
            </div>
          </div>
          <p>{assembly.description}</p>

          <AttachmentList items={assembly.attachments} />
          {assembly.subjects.map(subject => (
            <div className="mb-3" key={subject._id}>
              <SubjectCard assembly={assembly} subject={subject} refetch={() => refetch()} />
            </div>
          ))}
          {isJanitor && assembly.status === 'draft' && (
            <button className="btn btn-primary btn-block" type="button" onClick={() => open()} disabled={opening}>
              {`${opening ? 'Abrindo' : 'Abrir'} ${readableAssembly.title.toLowerCase()}`}
            </button>
          )}
          {isJanitor && assembly.status === 'closed' && (
            <Link
              className="btn btn-outline-secondary btn-block"
              to={`/condominiums/${assembly.condominium}/${readableAssembly.path}/${assembly._id}/subjects`}
              style={{ marginBottom: 40 }}
            >
              Ver resultado detalhado
            </Link>
          )}
        </>
      )}
    </div>
  );
}

AssemblyDetail.propTypes = {
  match: propTypes.object.isRequired,
  history: propTypes.object.isRequired,
};
